import React, { useState, useEffect } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { inventoryItemByLocationByStatusMini } from "../../../../graphql/inventory/custom-queries";

import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  makeStyles,
  Menu,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { blueGrey, grey, green, deepOrange } from "@material-ui/core/colors";
import { alpha } from "@material-ui/core/styles/colorManipulator";

import {
  Confirm,
  Edit,
  Loading,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
  TopToolbar,
  useEditController,
  useNotify,
  useRefresh,
} from "react-admin";
import { useForm } from "react-final-form";

import { AddIcon } from "../../../../components/common/iconography";
import SwitchOnOff from "../../../../components/common/AntSwitch";
import { ForceTitle } from "../../../../components/common/ForceTitle";
import { HelpButton } from "../../../../components/common/HelpButton";
import { BackButton } from "../../../../components/common/BackButton";

import {
  InventoryStatus,
  LocationLoadType,
  LocationType,
} from "../../../../utils/constants";
import {
  doUpdateLocation,
  getLocationFullName,
  isBulkLocation,
  isFloorLoaded,
} from "../../../../utils/locationUtils";
import { logError } from "../../../../utils/appUtils";
import { StructuredLocationDialog } from "../StructuredLocationDialog";
import { StructuredLocationMap } from "../StructuredLocationMap";
import { getBackPath, handleNumberFieldKeyDown } from "../../../../utils/utils";
import { BulkLocationMap } from "../BulkLocationMap";
import {
  createNewLocation,
  doDeleteLocation,
  doUpdateAisle,
  getZones,
  handleAddBulkLocations,
  setupAisle,
  updateLocationPosition,
} from "./aisleUtils";
import { AisleEditToolbar } from "./AisleEditToolbar";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    paddingLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  item: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    width: "100%",
  },
  tagItem: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: theme.spacing(0.5),
  },
  addTagButton: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(0.2),
  },
  tagContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    flexWrap: "wrap",
    border: "1px dashed",
    borderRadius: theme.spacing(1),
    borderColor: grey[400],
    padding: theme.spacing(1),
    minHeight: theme.spacing(8),
  },
  tagsTitle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  tagActionsContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: theme.spacing(1),
  },
  locationUsedAvatar: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.getContrastText(deepOrange[900]),
    backgroundColor: deepOrange[700],
    margin: theme.spacing(0.5),
    whiteSpace: "nowrap",
    border: "1px solid",
    borderColor: deepOrange[500],
    borderRadius: 2,
    "&:hover": {
      cursor: "pointer",
      backgroundColor: alpha(deepOrange[500], 0.12),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    width: "100%",
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  locationAvatar: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.getContrastText(green[600]),
    backgroundColor: green[600],
    margin: theme.spacing(0.5),
    whiteSpace: "nowrap",
    border: "1px solid",
    borderColor: green[600],
    borderRadius: 2,
    "&:hover": {
      cursor: "pointer",
      backgroundColor: alpha(green[600], 0.12),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    width: "100%",
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  menu: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    backgroundColor: blueGrey[500],
    width: "100%",
  },
  location: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    padding: theme.spacing(1),
    minHeight: theme.spacing(8),
    marginLeft: theme.spacing(0.75),
    marginTop: theme.spacing(1),
  },
  locationInput: {
    marginRight: theme.spacing(2),
    minWidth: "8rem",
  },
  capacityInput: {
    margin: theme.spacing(0.5),
    minWidth: "6rem",
  },
  dialogInput: {
    paddingRight: theme.spacing(1),
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  bayContainer: {
    display: "flex",
    flexDirection: "column-reverse",
    alignItems: "center",
    alignContent: "space-between",
    border: "1px solid #b0bec5",
    borderRadius: theme.spacing(1),
    margin: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    paddingTop: theme.spacing(1),
  },
  divider: {
    borderTop: "1px solid #b0bec5",
    marginTop: theme.spacing(1),
    width: "100%",
  },
  levelContainer: {
    border: "1px solid",
    borderColor: blueGrey[500],
    // margin: theme.spacing(.5),
    display: "flex",
    flexDirection: "row",
    width: "100%",
  },
  typeCapacityContainer: {
    marginTop: theme.spacing(2),
  },
  formControl: {
    marginTop: theme.spacing(1),
  },
}));

const AisleEditActions = (props) => {
  return (
    <TopToolbar>
      {/* <HelpButton>
        <InboundEditHelp {...props} />
      </HelpButton> */}
      <BackButton {...props} />
    </TopToolbar>
  );
};

const updateSelectedAisle = async (newAisle) => {
  await doUpdateAisle(newAisle, false);
};

const getMaxBayLevels = (bayList = []) => {
  return bayList.reduce(
    (maxLevels, bay) => Math.max(maxLevels, bay?.length || 0),
    0,
  );
};

const AisleEdit = (props) => {
  const classes = useStyles();
  const notify = useNotify();
  const refresh = useRefresh();
  const { record } = useEditController(props);
  const backPath = getBackPath(props);

  const [loading, setLoading] = useState();

  const [bayList, setBayList] = useState([]);
  const [locationsPerLevel, setLocationsPerLevel] = useState(1);
  const [levels, setLevels] = useState(1);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [zones, setZones] = useState([]);
  const [selectedZoneId, setSelectedZoneId] = useState("");
  const [isContainer, setIsContainer] = useState(false);

  useEffect(() => {
    if (record) {
      setupAisle(
        record,
        selectedLocation,
        setSelectedLocation,
        setIsContainer,
        setBayList,
        notify,
      );
      getZones(record, setZones, setSelectedZoneId, notify);
      setLevels(record.levels || 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit Aisle" />}
      mutationMode="pessimistic"
      actions={<AisleEditActions {...props} backPath={backPath} />}
      // aside={
      //   <InboundAside
      //     {...props}
      //     backPath={backPath}
      //     record={record}
      //   />
      // }
    >
      <SimpleForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={
          <AisleEditToolbar
            {...props}
            record={record}
            backPath={backPath}
            classes={classes}
            notify={notify}
          />
        }
        // validate={(values) => validateInboundEdit(record, values)}
      >
        <AisleEditForm
          {...props}
          record={record}
          bayList={bayList}
          zones={zones}
          selectedZoneId={selectedZoneId}
          setSelectedZoneId={setSelectedZoneId}
          levels={levels}
          setLevels={setLevels}
          locationsPerLevel={locationsPerLevel}
          setLocationsPerLevel={setLocationsPerLevel}
          isContainer={isContainer}
          setLoading={setLoading}
          classes={classes}
          notify={notify}
          refresh={refresh}
        />
        <LocationMap
          {...props}
          record={record}
          bayList={bayList}
          setBayList={setBayList}
          locationsPerLevel={locationsPerLevel}
          setLevels={setLevels}
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          isContainer={isContainer}
          loading={loading}
          setLoading={setLoading}
          classes={classes}
          notify={notify}
          refresh={refresh}
        />
      </SimpleForm>
    </Edit>
  );
};

const AisleEditForm = ({
  record,
  bayList,
  zones,
  setSelectedZoneId,
  levels,
  setLevels,
  locationsPerLevel,
  setLocationsPerLevel,
  isContainer,
  setLoading,
  classes,
  notify,
  refresh,
  ...props
}) => {
  const MAX_LEVELS = 5;
  const levelsList = Array.from({ length: MAX_LEVELS }, (_, i) => i + 1);
  const smDown = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const form = useForm();
  const [isTopDown, setIsTopDown] = useState(record.topDownPickOrder);
  const [locationsPerBayBulkError, setLocationsPerBayBulkError] =
    useState(false);

  // Subscribe to field state updates for 3rd party inputs
  form.registerField(
    "topDownPickOrder",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );

  const handleAddBay = async (evt) => {
    try {
      debugger;
      setLoading(true);
      const submitFlag = !!evt.preventDefault;
      if (submitFlag) {
        evt.preventDefault();
      }
      if (record.type === LocationType.BULK) {
        await handleAddBulkLocations(
          record,
          locationsPerLevel,
          setLocationsPerBayBulkError,
          updateSelectedAisle,
          setLoading,
          notify,
        );
        return;
      }

      const promiseList = [];
      const bayNumber = bayList.length;
      bayList.push([]);
      for (let j = 1; j <= levels; j++) {
        for (let k = 1; k <= locationsPerLevel; k++) {
          promiseList.push(
            createNewLocation(record, bayList, isContainer, bayNumber, j, k),
          );
        }
      }
      await Promise.all(promiseList);
      const nextLevels = Math.max(
        getMaxBayLevels(bayList),
        levels,
        1,
      );
      setLevels(nextLevels);
      const aisle = {
        ...record,
        bays: bayList.length,
        levels: nextLevels,
        totalLocations: record.locations.items.length + promiseList.length,
      };
      await updateSelectedAisle(aisle);
    } catch (err) {
      logError("reloadYard:AisleEdit:handleAddBay", err, notify);
    } finally {
      setLoading(false);
      refresh();
    }
  };

  const getAislePrefix = () => {
    const areaPrefix = record.area.prefix;
    const zonePrefix = record.zone.prefix;
    const value = `${areaPrefix}-${zonePrefix}`;
    return value;
  };

  const handleLocationsPerBayChange = (evt) => {
    debugger;
    if (record.type === LocationType.BULK) {
      setLocationsPerBayBulkError(false);
      // setBays(1);
      // setLevels(1);
    }
    setLocationsPerLevel(evt.target.value ? Number(evt.target.value) : "");
  };

  const isZoneSelectDisabled = () => {
    // return record.locations.items
    //   .filter((item) => item.active === "true")
    //   ?.some((location) => location.productId);
    return false;
  };

  const handleZoneChange = (event) => {
    const newZone = zones.find((itm) => itm.id === event.target.value);
    form.change("zone", newZone);
  };

  const handleTopDownChange = (value) => {
    setIsTopDown(value);
    form.change("topDownPickOrder", value);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={10}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <TextInput
              source="name"
              label="Aisle Name"
              variant="outlined"
              fullWidth
              validate={required()}
              parse={(v) => v.toUpperCase()}
              helperText={false}
              inputProps={{ style: { textTransform: "uppercase" } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {getAislePrefix()}{" "}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <SelectInput
              source="zoneId"
              label="Zone"
              variant="outlined"
              fullWidth
              choices={zones}
              validate={required()}
              disabled={isZoneSelectDisabled()}
              onChange={handleZoneChange}
              helperText={false}
            />
          </Grid>
          {record.type !== LocationType.BULK &&
            !record.locationLoad?.container?.selected && (
              <Grid item xs={3}>
                <Box display="flex" mt={1}>
                  <SwitchOnOff
                    fullWidth
                    leftLabel="Racks"
                    rightLabel="Floor-loaded"
                    checked={isTopDown}
                    setChecked={handleTopDownChange}
                  />
                </Box>
              </Grid>
            )}
        </Grid>
      </Grid>
      <Grid item xs={10}>
        <Grid container spacing={2}>
          {record.type !== LocationType.BULK && (
            <Grid item xs={3}>
              <TextField
                select
                name="levels"
                label="No. of Levels"
                size="small"
                variant="outlined"
                fullWidth
                onChange={(evt) => setLevels(Number(evt.target.value))}
                value={levels}
                className={classes.locationInput}
              >
                {levelsList.map((itm) => (
                  <MenuItem key={itm} value={itm}>
                    {itm}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          {record.type === LocationType.BULK ? (
            <Grid item xs={3}>
              <TextField
                id="bulk-positions"
                label="No of Locations"
                size="small"
                variant="outlined"
                type="number"
                inputProps={{ min: 1 }}
                fullWidth
                required
                value={locationsPerLevel}
                error={locationsPerBayBulkError}
                helperText={locationsPerBayBulkError && "Required"}
                onChange={handleLocationsPerBayChange}
                onKeyDown={handleNumberFieldKeyDown}
              />
            </Grid>
          ) : (
            <Grid item xs={3}>
              <TextField
                select
                name="positions"
                label="Locations/Level"
                size="small"
                variant="outlined"
                fullWidth
                onChange={(evt) =>
                  setLocationsPerLevel(Number(evt.target.value))
                }
                value={locationsPerLevel}
                className={classes.locationInput}
                disabled={isContainer}
              >
                {isContainer
                  ? [
                      <MenuItem key={1} value={1}>
                        1
                      </MenuItem>,
                    ]
                  : levelsList.map((itm) => (
                      <MenuItem key={itm} value={itm}>
                        {itm}
                      </MenuItem>
                    ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={3}>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddBay}
            >
              {smDown
                ? "Add"
                : record.type === LocationType.BULK
                  ? "Add Locations"
                  : "Add Bay"}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
    </Grid>
  );
};

const LocationMap = ({
  record,
  bayList,
  setBayList,
  locationsPerLevel,
  setLevels,
  selectedLocation,
  setSelectedLocation,
  isContainer,
  loading,
  setLoading,
  classes,
  notify,
  refresh,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [confirmContent, setConfirmContent] = useState("");
  const [selectedBay, setSelectedBay] = useState("");
  const [selectedBayIndex, setSelectedBayIndex] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState("");
  // const [locationType, setLocationType] = useState("storage"); // storage | staging | inbound | outbound ...
  const [anchorEl, setAnchorEl] = React.useState(null);

  const openEditDialog = () => {
    handleMenuClose();
    setOpen(true);
  };

  const handleMenuOpen = (event, location) => {
    debugger;
    // if (location.locationLoad?.container?.selected) return;

    setSelectedLocation(location);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getDeleteLocationOperation = async (locations) => {
    const locationList = Array.isArray(locations) ? locations : [locations];
    let isStored, isUnloaded, isReview, isShipped, isHeld;
    let promiseList = [];
    debugger;

    if (!locations || locationList.length === 0) return "ABORT";

    locationList.forEach((location) => {
      const searchQuery = {
        locationId: location.id,
        status: { beginsWith: InventoryStatus.STORED },
        limit: 1,
      };
      promiseList.push(
        API.graphql(
          graphqlOperation(inventoryItemByLocationByStatusMini, searchQuery),
        ),
      );
    });
    let results = await Promise.all(promiseList);
    results.forEach((result) => {
      if (result.data.inventoryItemByLocationByStatus.items.length > 0)
        isStored = true;
    });

    if (!isStored) {
      promiseList = [];
      locationList.forEach((location) => {
        const searchQuery = {
          locationId: location.id,
          status: { beginsWith: InventoryStatus.UNLOADED },
          limit: 1,
        };
        promiseList.push(
          API.graphql(
            graphqlOperation(inventoryItemByLocationByStatusMini, searchQuery),
          ),
        );
      });
      results = await Promise.all(promiseList);
      results.forEach((result) => {
        if (result.data.inventoryItemByLocationByStatus.items.length > 0)
          isUnloaded = true;
      });
    }

    if (!isStored && !isUnloaded) {
      promiseList = [];
      locationList.forEach((location) => {
        const searchQuery = {
          locationId: location.id,
          status: { beginsWith: InventoryStatus.REVIEW },
          limit: 1,
        };
        promiseList.push(
          API.graphql(
            graphqlOperation(inventoryItemByLocationByStatusMini, searchQuery),
          ),
        );
      });
      results = await Promise.all(promiseList);
      results.forEach((result) => {
        if (result.data.inventoryItemByLocationByStatus.items.length > 0)
          isReview = true;
      });
    }

    isHeld = isStored || isUnloaded || isReview;

    if (!isHeld) {
      promiseList = [];
      locationList.forEach((location) => {
        const searchQuery = {
          locationId: location.id,
          status: { beginsWith: InventoryStatus.SHIPPED },
          limit: 1,
        };
        promiseList.push(
          API.graphql(
            graphqlOperation(inventoryItemByLocationByStatusMini, searchQuery),
          ),
        );
      });
      results = await Promise.all(promiseList);
      results.forEach((result) => {
        if (result.data.inventoryItemByLocationByStatus.items.length > 0)
          isShipped = true;
      });
    }

    debugger;
    if (isHeld) {
      return "ABORT";
    } else if (isShipped) {
      return "INACTIVATE";
    }

    return "DELETE";
  };

  const handleAddLocationToLevel = async (updateList, position) => {
    try {
      setLoading(true);
      handleMenuClose();
      debugger;
      const promiseList = updateList.map((itm) => updateLocationPosition(itm));
      await Promise.all(promiseList);
      debugger;
      await createNewLocation(
        record,
        bayList,
        isContainer,
        selectedLocation.bay - 1,
        selectedLocation.level,
        position,
      );
      const aisle = {
        ...record,
        totalLocations: record.locations.items.length + 1,
      };
      await updateSelectedAisle(aisle);
    } catch (err) {
      logError("reloadYard:AisleEdit:handleAddLocationToLevel", err, notify);
    } finally {
      setLoading(false);
      refresh();
    }
  };

  const handleClose = (evt) => {
    const submitFlag = !!evt?.preventDefault;
    if (submitFlag) {
      evt.preventDefault();
    }
    setSelectedLocation(null);
    setOpen(false);
  };

  const handleDeleteCancel = () => {
    setOpenDelete(false);
    setSelectedBay(null);
    setSelectedBayIndex(null);
    setSelectedLevel(null);
    setSelectedLocation(null);
  };

  const confirmDeleteBay = (bayNumber) => {
    if (!bayNumber) return;
    const selectedBay = bayList[bayNumber];
    if (selectedBay.length === 0) return;
    setSelectedBay(selectedBay);
    setSelectedBayIndex(bayNumber);
    setConfirmContent(
      `Deleting a Bay will delete all of its locations, are you sure you want to delete Bay ${
        bayNumber + 1
      }`,
    );
    setOpenDelete(true);
  };

  const handleDeleteBay = async () => {
    let logTxt = "reloadYard:Location:handleDeleteBay";
    try {
      debugger;
      setLoading(true);
      setOpenDelete(false);
      const bayLocations = getBayLocations(selectedBay);
      const operation = await getDeleteLocationOperation(bayLocations);
      let promiseList = [];
      if (operation === "ABORT") {
        notify("Bay has Locations that are in use!", "warning");
        return Promise.resolve();
      } else if (operation === "INACTIVATE") {
        logTxt = "reloadYard:Location:handleDeleteBay:inactivate";
        for (let location of bayLocations) {
          location.active = "false";
          promiseList.push(doUpdateLocation(location));
        }
        await Promise.all(promiseList);
      } else {
        for (let location of bayLocations) {
          promiseList.push(doDeleteLocation(location));
        }
        await Promise.all(promiseList);
      }
      const nextBayList = bayList.filter((_, idx) => idx !== selectedBayIndex);
      const nextLevels = Math.max(getMaxBayLevels(nextBayList), 1);
      setBayList(nextBayList);
      setLevels(nextLevels);
      setSelectedLevel(null);
      setSelectedLocation(null);
      setSelectedBay(null);
      setSelectedBayIndex(null);

      const aisle = {
        ...record,
        bays: nextBayList.length,
        levels: nextLevels,
        totalLocations: record.locations.items.length - promiseList.length,
      };
      await updateSelectedAisle(aisle);
    } catch (err) {
      logError(logTxt, err, notify);
    } finally {
      setLoading(false);
      refresh();
    }
  };

  const handleAddLevel = async (bayNumber) => {
    try {
      setLoading(true);
      handleMenuClose();
      const selectedBay = bayList[bayNumber];
      const promiseList = [];
      let locationsToCreate = locationsPerLevel;
      if (isFloorLoaded(record)) {
        debugger;
        const topLevel = selectedBay[selectedBay.length - 1];
        locationsToCreate = topLevel.length;
      }
      for (let i = 1; i <= locationsToCreate; i++) {
        promiseList.push(
          createNewLocation(
            record,
            bayList,
            isContainer,
            bayNumber,
            selectedBay.length + 1,
            i,
          ),
        );
      }
      await Promise.all(promiseList);
      const nextLevels = Math.max(
        getMaxBayLevels(bayList),
        selectedBay.length + 1,
        1,
      );
      setLevels(nextLevels);
      const aisle = {
        ...record,
        levels: nextLevels,
        totalLocations: record.locations.items.length + promiseList.length,
      };
      await updateSelectedAisle(aisle);
    } catch (err) {
      logError("reloadYard:AisleEdit:handleAddLevel", err, notify);
    } finally {
      setLoading(false);
      refresh();
    }
  };

  const confirmDeleteLevel = () => {
    debugger;
    const selectedBay = bayList[selectedLocation.bay - 1];
    const selectedBayLevel = selectedBay[selectedBay.length - 1];
    if (selectedBayLevel.length === 0) return;
    handleMenuClose();
    setSelectedLevel(selectedBayLevel);
    setConfirmContent(
      `Deleting a Level will delete all of its locations, are you sure you want to delete this Level?`,
    );
    setOpenDelete(true);
  };

  const handleDeleteLevel = async () => {
    let logTxt = "reloadYard:AisleEdit:handleDeleteLevel";
    debugger;

    try {
      setLoading(true);
      setOpenDelete(false);

      const levelLocations = selectedLevel;
      const operation = await getDeleteLocationOperation(levelLocations);

      const promiseList = [];
      if (operation === "ABORT") {
        notify("Level has Locations that are in use!", "warning");
        return Promise.resolve();
      } else if (operation === "INACTIVATE") {
        logTxt = "reloadYard:AisleEdit:handleDeleteLevel:inactivate";
        for (let location of levelLocations) {
          location.active = "false";
          promiseList.push(doUpdateLocation(location));
        }
        await Promise.all(promiseList);
      } else {
        const promiseList = levelLocations.map((itm) => doDeleteLocation(itm));
        await Promise.all(promiseList);
      }

      const nextBayList = bayList.map((bay, idx) => {
        if (idx !== selectedLocation.bay - 1) return bay;
        const nextBay = [...bay];
        nextBay.splice(nextBay.length - 1, 1);
        return nextBay;
      });
      const nextLevels = Math.max(getMaxBayLevels(nextBayList), 1);
      setBayList(nextBayList);
      setLevels(nextLevels);
      setSelectedLevel(null);
      setSelectedLocation(null);
      setSelectedBay(null);
      setSelectedBayIndex(null);
      const aisle = {
        ...record,
        levels: nextLevels,
        totalLocations: record.locations.items.length - promiseList.length,
      };
      await updateSelectedAisle(aisle);
    } catch (err) {
      logError(logTxt, err, notify);
    } finally {
      setLoading(false);
      setOpenDelete(false);
      refresh();
    }
  };

  const canDeleteLocation = () => {
    if (!selectedLocation) return false;
    if (record.type === LocationType.BULK) return true;

    const selectedBay = bayList[selectedLocation.bay - 1];
    const selectedLevel =
      selectedBay && selectedBay[selectedLocation.level - 1];
    if (!selectedLevel) return false;
    const selectedBayLevel = [...selectedLevel];
    if (selectedBayLevel.length === 1) return false;
    if (selectedLocation.position !== selectedBayLevel.length) return false;

    if (isFloorLoaded(selectedLocation)) {
      // cannot delete location in the middle of stack, or if another location above exists
      const selectedBay = bayList[selectedLocation.bay - 1];
      const bayLevelAbove = selectedBay[selectedLocation.level];
      const locationAbove = bayLevelAbove
        ? bayLevelAbove[selectedLocation.position - 1]
        : null;
      return locationAbove ? false : true;
    }

    return true;
  };

  const canAddLocation = () => {
    debugger;
    if (!selectedLocation) return false;
    if (record.type === LocationType.BULK) return true;

    const selectedBay = bayList[selectedLocation.bay - 1];
    const selectedLevel =
      selectedBay && selectedBay[selectedLocation.level - 1];
    if (!selectedLevel) return false;
    const selectedBayLevel = [...selectedLevel];
    if (selectedLocation.position !== selectedBayLevel.length) return false;

    if (isFloorLoaded(selectedLocation)) {
      // cannot add location in the middle of stack, or if location below does not exists
      const selectedBay = bayList[selectedLocation.bay - 1];
      const bayLevelBelow = selectedBay[selectedLocation.level - 2];
      const locationBelowToRight = bayLevelBelow
        ? bayLevelBelow[selectedLocation.position]
        : null;
      const isRightmostFirstLevel =
        selectedLocation.position === selectedBayLevel.length &&
        selectedLocation.level === 1;
      return locationBelowToRight || isRightmostFirstLevel ? true : false;
    }

    return true;
  };

  const setInventoryType = (bayNumber) => {
    debugger;
  };

  const canDeleteLevel = () => {
    if (!selectedLocation) return false;
    const selectedBay = bayList[selectedLocation.bay - 1];
    if (selectedBay.length > 1 && selectedBay.length === selectedLocation.level)
      return true;
    return false;
  };

  const showCapacityUsed = () => {
    return (
      selectedLocation &&
      !selectedLocation.locationLoad[LocationLoadType.CONTAINER]?.selected
    );
  };

  // const handleTypeChange = (evt) => {
  //   const type = evt.target.vale;
  //   setLocationType(type);
  // };

  const getBayLocations = (bay) => {
    const locationList = [];

    for (let level of bay) {
      for (let location of level) {
        locationList.push(location);
      }
    }
    return locationList;
  };

  const handleAddLocationRight = async () => {
    const selectedBayLevel = [
      ...bayList[selectedLocation.bay - 1][selectedLocation.level - 1],
    ];
    selectedBayLevel.splice(0, selectedLocation.position);
    await handleAddLocationToLevel(
      selectedBayLevel,
      selectedLocation.position + 1,
    );
  };

  // const handleAddLocationLeft = async () => {
  //     const selectedBayLevel = [...bayList[selectedLocation.bay - 1][selectedLocation.level - 1]];
  //     const rightList = selectedBayLevel.splice(selectedLocation.position - 1);
  //     await handleAddLocationToLevel(rightList, selectedLocation.position);
  // }

  const confirmDeleteLocation = (location) => {
    debugger;
    if (!isBulkLocation(location)) {
      const selectedBayLevel = [
        ...bayList[location.bay - 1][location.level - 1],
      ];
      if (selectedBayLevel.length === 1) return;
      if (location.position !== selectedBayLevel.length) return;
      handleMenuClose();
    }

    setSelectedLocation(location);
    setConfirmContent(
      `Are you sure you want to delete location ${getLocationFullName(
        location,
      )}?`,
    );
    setOpenDelete(true);
  };

  const handleDeleteLocation = async () => {
    let logTxt = "reloadYard:AisleEdit:handleDeleteLocation";
    debugger;
    try {
      setLoading(true);
      setOpenDelete(false);
      const operation = await getDeleteLocationOperation(selectedLocation);
      if (operation === "ABORT") {
        notify("This Location is in use!", "warning");
        return Promise.resolve();
      } else if (operation === "INACTIVATE") {
        logTxt = "reloadYard:AisleEdit:handleDeleteLocation:inactivate";
        selectedLocation.active = "false";
        await doUpdateLocation(selectedLocation);
      } else {
        await doDeleteLocation(selectedLocation);
      }
      setSelectedLevel(null);
      setSelectedLocation(null);
      setSelectedBay(null);
      const aisle = {
        ...record,
        totalLocations: record.totalLocations - 1,
      };
      await updateSelectedAisle(aisle);
    } catch (err) {
      logError(logTxt, err, notify);
    } finally {
      setLoading(false);
      setOpenDelete(false);
      refresh();
    }
  };

  const getBadgeContent = (location) => {
    let badge = "";
    if (location.locationLoad[LocationLoadType.PALLET]?.selected) badge += "P";
    if (location.locationLoad[LocationLoadType.BOX]?.selected) badge += "B";
    if (location.locationLoad[LocationLoadType.SUPERSACK]?.selected)
      badge += "S";
    if (location.locationLoad[LocationLoadType.CONTAINER]?.selected)
      badge += "C";
    if (location.locationLoad[LocationLoadType.DRY_BULK]?.selected)
      badge += "D";
    if (location.locationLoad[LocationLoadType.LIQUID_BULK]?.selected)
      badge += "L";
    if (location.locationLoad[LocationLoadType.OTHER]?.selected) badge += "O";

    return badge;
  };

  const handleBulkLocationClick = (location) => {
    debugger;
    setSelectedLocation(location);
    setOpen(true);
  };

  if (!record || bayList.length === 0) return <Loading />;
  return (
    <>
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {record.type === LocationType.BULK ? (
        <BulkLocationMap
          classes={classes}
          aisle={record}
          handleBulkLocationClick={handleBulkLocationClick}
          confirmDeleteLocation={confirmDeleteLocation}
          handleAddBulkLocations={handleAddBulkLocations}
        />
      ) : (
        <StructuredLocationMap
          classes={classes}
          bayList={bayList}
          confirmDeleteBay={confirmDeleteBay}
          handleAddLevel={handleAddLevel}
          setInventoryType={setInventoryType}
          setBayList={setBayList}
          getBadgeContent={getBadgeContent}
          handleMenuOpen={handleMenuOpen}
        />
      )}
      {selectedLocation && (
        <>
          <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={openEditDialog}>Edit location</MenuItem>
            {canDeleteLocation() && (
              <MenuItem onClick={() => confirmDeleteLocation(selectedLocation)}>
                Delete location
              </MenuItem>
            )}
            {canAddLocation() && (
              <MenuItem onClick={handleAddLocationRight}>Add location</MenuItem>
            )}
            {canDeleteLevel() && (
              <MenuItem onClick={confirmDeleteLevel}>Delete level</MenuItem>
            )}
            {showCapacityUsed() && (
              <MenuItem disabled>
                <Typography>
                  Capacity Used: {selectedLocation.capacityUsed || 0}%
                </Typography>
              </MenuItem>
            )}
          </Menu>
          {open && (
            <StructuredLocationDialog
              location={selectedLocation}
              aisle={record}
              classes={classes}
              open={open}
              handleClose={handleClose}
              refresh={refresh}
              updateSelectedAisle={updateSelectedAisle}
              // locationType={locationType}
              // handleTypeChange={handleTypeChange}
            />
          )}
        </>
      )}
      <Confirm
        isOpen={openDelete}
        title={"Delete Confirmation"}
        content={confirmContent}
        onConfirm={
          selectedBay
            ? handleDeleteBay
            : selectedLevel
              ? handleDeleteLevel
              : handleDeleteLocation
        }
        onClose={handleDeleteCancel}
        loading={loading}
      />
    </>
  );
};

export default AisleEdit;
